import { UniButton } from '@/design-system/components/buttons';
import { TextBody, TextCaption, H6 } from '@/design-system/components/typography';
import { ScrollView, SxProp, View } from 'dripsy';
import { isEmpty, isString } from 'lodash';
import React, { useContext, useCallback, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutContext } from 'contexts/LayoutContext';
import { BottomSheetHeader } from '@/design-system/components/headers';
import BottomSheet, { useBottomSheetDynamicSnapPoints, BottomSheetBackdrop } from '@gorhom/bottom-sheet';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { WithdrawStackParamList, WithdrawStackRoutes } from './type';
import { AlertType, UniAlert } from '@/design-system/components/alerts';
import { errorParser } from '@/features/bill-payment/screens/otp/type';
import { formatAmountByDecimal } from '../../utils';
import { Xmetadata } from '@utx/utx-access-client';

export interface WithdrawProps {
  onClose: () => void;
  onLayout: (event: any) => void;
  data: any;
  func?: (
    accountId: string,
    amount: number,
    currency: string,
    xmetadata: Xmetadata,
    onSuccess?: (data: any) => void,
    onError?: (error: any) => void
  ) => void;
  onReturnData?: (data: any) => void;
  refresh?: () => void;
  record?: any;
}

export const WithdrawNativeConfirmScreen: React.FC<WithdrawProps> = (props) => {
  const { onClose, onLayout, data, func, onReturnData, refresh, record } = props;
  const { t } = useTranslation('account-management');
  const { body, textTitle, textAtmPin, btnView, btn, modal, topHeader } = sxStyles;
  const [loading, setLoading] = useState(false);

  const layout = useContext(LayoutContext);
  const renderBackdrop = useCallback(
    (props) => <BottomSheetBackdrop {...props} disappearsOnIndex={0} appearsOnIndex={1} pressBehavior={'none'} enableTouchThrough={true} />,
    []
  );
  const { width, height } = layout.dimensions;
  const { animatedHandleHeight, animatedSnapPoints, animatedContentHeight, handleContentLayout } = useBottomSheetDynamicSnapPoints([
    'CONTENT_HEIGHT',
    'CONTENT_HEIGHT'
  ]);

  const navigation = useNavigation<NativeStackNavigationProp<WithdrawStackParamList>>();
  const [alertMessage, setAlertMessage] = useState('');

  const snapPoints = useMemo(() => ['50%', '90%'], []);
  return (
    <View sx={{ width, height, backgroundColor: 'transparent' }}>
      <BottomSheet enablePanDownToClose index={1} snapPoints={snapPoints} backdropComponent={renderBackdrop}>
        <ScrollView sx={{ backgroundColor: '$backgroundPrimary' }} onLayout={handleContentLayout}>
          <View sx={modal}>
            <View sx={topHeader}>
              <BottomSheetHeader
                title={t('card-account-management::withdraw-confirmation.title--withdraw-confirmation')}
                onPress={() => navigation.popToTop()}
              />
            </View>
            <View sx={body}>
              <TextBody semiBold>{t('card-account-management::withdraw-confirmation.text--are-you-sure-to-withdraw')}</TextBody>
              <View>
                <TextCaption sx={textTitle} semiBold>
                  {t('card-account-management::glossary.from-account')}
                </TextCaption>
                <H6 sx={{ ...textAtmPin, mb: '$1' }}>
                  {t('card-account-management::glossary.card-account')} - {record?.name}
                </H6>
              </View>
              <View>
                <TextCaption sx={textTitle} semiBold>
                  {t('card-account-management::glossary.to-account')}
                </TextCaption>
                <H6 sx={{ ...textAtmPin, mb: '$1' }}>{data.wallet.label}</H6>
                <TextBody>{data.wallet.accountNumber}</TextBody>
              </View>
              <View>
                <TextCaption sx={textTitle} semiBold>
                  {t('card-account-management::glossary.amount')}
                </TextCaption>
                <H6 sx={textAtmPin}>
                  {formatAmountByDecimal({ value: data.amount, decimals: 2 })} {record.currency}
                </H6>
              </View>
              <View>
                <TextCaption sx={textTitle} bold>
                  {t('card-account-management::glossary.xmetadata')}
                </TextCaption>
                {Object.keys(data.xmetadata).map((k) => (
                  <View sx={{ pl: '$5', my: '$3' }}>
                    <TextCaption sx={textTitle} semiBold>
                      {k}
                    </TextCaption>
                    <H6 sx={textAtmPin}>{data.xmetadata[k]}</H6>
                  </View>
                ))}
              </View>
              {!isEmpty(alertMessage) && <UniAlert type={AlertType.Error} message={alertMessage} sx={{ mt: 5 }} />}
              <View sx={btnView}>
                <UniButton
                  sx={btn}
                  loading={loading}
                  labelSx={{ color: '$balck' }}
                  label={t('card-account-management::general.button--place-instruction')}
                  onPress={() => {
                    if (func) {
                      setLoading(true);
                      func(
                        data.accountId,
                        data.amount,
                        data.currency,
                        data.xmetadata,
                        (returnData) => {
                          setLoading(false);
                          onReturnData && onReturnData(returnData.data);
                          navigation.navigate(WithdrawStackRoutes.Placed, {
                            record,
                            onLayout: onLayout,
                            data,
                            returnData: returnData.data,
                            refresh
                          });
                        },
                        (error) => {
                          setLoading(false);
                          const data: any = error.response?.data;
                          const errorMessage =
                            data && isString(data.message) ? errorParser(data.message, t) : errorParser(error.message, t);
                          setAlertMessage(errorMessage);
                        }
                      );
                    }
                  }}
                />
              </View>
            </View>
          </View>
        </ScrollView>
      </BottomSheet>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  modal: {
    width: '100%',
    padding: '$4'
  },
  body: {
    gap: '$5'
  },
  textTitle: {
    marginBottom: 4,
    color: '$textSecondary'
  },
  textAtmPin: {
    fontWeight: 'bolder',
    color: '$textThemeSecondary'
  },
  btnView: {
    flex: 1,
    display: 'grid',
    gap: '$3',
    mt: '$4'
  },
  btn: {
    width: '100%',
    height: 44
  }
};
